// frontend/src/pages/MemberStatementPage.jsx
// CONSOLIDATED VERSION: Shows combined loan balances with "Add" columns for topup tracking
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import globalApi from '../utils/globalFetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function fmt(n, dp=2){
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
function asYyyyMm(d){
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth()+1).padStart(2,'0');
  return `${y}-${m}`;
}

// ── Searchable member picker ──────────────────────────────────────────────────
function MemberSearch({ members, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Keep input label in sync when value is set externally
  const selected = members.find(m => m.memberId === value);
  const displayLabel = selected
    ? `${selected.memberId} - ${selected.personalInfo?.fullName || ''}`
    : '';

  // When dropdown closes without a selection, restore the label
  const inputValue = open ? query : displayLabel;

  const filtered = query.trim()
    ? members.filter(m => {
        const q = query.toLowerCase();
        return (
          m.memberId?.toLowerCase().includes(q) ||
          (m.personalInfo?.fullName || '').toLowerCase().includes(q)
        );
      })
    : members;

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(m) {
    onChange(m.memberId);
    setQuery('');
    setOpen(false);
  }

  function handleInputChange(e) {
    setQuery(e.target.value);
    setOpen(true);
    if (!e.target.value) onChange(''); // clear selection if text cleared
  }

  function handleFocus() {
    setQuery('');
    setOpen(true);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder="Type ID or name to search..."
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        style={{
          width: '100%',
          padding: '6px 10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '14px',
          boxSizing: 'border-box'
        }}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #ccc',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          maxHeight: '260px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px', color: '#888', fontSize: '13px' }}>No members found</div>
          ) : (
            filtered.map(m => (
              <div
                key={m.id || m.memberId}
                onMouseDown={() => handleSelect(m)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  background: m.memberId === value ? '#ede9fe' : 'white',
                  fontWeight: m.memberId === value ? 600 : 400,
                  borderBottom: '1px solid #f0f0f0'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f3f0ff'}
                onMouseLeave={e => e.currentTarget.style.background = m.memberId === value ? '#ede9fe' : 'white'}
              >
                <span style={{ color: '#4b3ff6', fontWeight: 600 }}>{m.memberId}</span>
                {' — '}
                {m.personalInfo?.fullName || ''}
                {m.isLeft && <span style={{ marginLeft: 6, fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>Left</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function MemberStatementPage(){
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState('');
  const [fromMonth, setFromMonth] = useState(asYyyyMm(new Date(new Date().getFullYear(),0,1)));
  const [toMonth, setToMonth] = useState(asYyyyMm(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [savedData, setSavedData] = useState(null);
  const [savedView, setSavedView] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => { loadMembers(); }, []);

  // Auto-load when arriving from Monthly Processing click
  const autoLoadedRef = React.useRef(false);
  useEffect(() => {
    const paramMemberId = searchParams.get('memberId');
    const paramMonth = searchParams.get('month');
    if (paramMemberId && !autoLoadedRef.current) {
      autoLoadedRef.current = true;
      setMemberId(paramMemberId);
      const from = paramMonth || asYyyyMm(new Date(new Date().getFullYear(),0,1));
      const to = paramMonth || asYyyyMm(new Date());
      setFromMonth(from);
      setToMonth(to);
      // Auto-trigger statement load after state is set
      setTimeout(async () => {
        try {
          setErr(''); setLoading(true);
          const token = localStorage.getItem('hopeSaccoToken');
          const r = await fetch(`${API_URL}/reports/member-statement/${paramMemberId}?fromMonth=${from}&toMonth=${to}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          const j = await r.json();
          if (!j.success) throw new Error(j.message || 'Failed to load statement');
          setData(j.data);
        } catch(e){ setErr(e.message || 'Failed to load'); }
        finally{ setLoading(false); }
      }, 0);
    }
  }, [searchParams]);

  async function loadMembers(){
    try{
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/members-for-statement`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const j = await r.json();
      if (j.success) setMembers(j.data?.members || []);
    } catch(e){ console.error(e); }
  }

  async function loadStatement(){
    try{
      if (!memberId){ setErr('Select member'); return; }
      setErr(''); setLoading(true);
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/member-statement/${memberId}?fromMonth=${fromMonth}&toMonth=${toMonth}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message || 'Failed to load statement');
      setData(j.data);
    } catch(e){ setErr(e.message || 'Failed to load'); }
    finally{ setLoading(false); }
  }

  function displayMonthToKey(display) {
    const MONTHS = { Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12' };
    const parts = display.split('-');
    if (parts.length !== 2) return display;
    const mon = MONTHS[parts[0]]; const yr = `20${parts[1]}`;
    return mon ? `${yr}-${mon}` : display;
  }

  async function saveRecords() {
    if (!data || !memberId) return;
    try {
      setSaving(true); setSaveMsg('');
      const token = localStorage.getItem('hopeSaccoToken');
      const memberObj = members.find(m => m.memberId === memberId);
      const payload = {
        memberId,
        memberDocId: memberObj?.id || memberObj?.docId || '',
        memberName: memberObj?.personalInfo?.fullName || memberId,
        rows: data.statement.map(r => ({
          month:                        displayMonthToKey(r.month),
          shares:                       r.shares,
          sharesContribution:           r.sharesContribution,
          loanBalance:                  r.loanBalance,
          loanInterest:                 r.loanInterest,
          loanRepayment:                r.loanRepayment,
          additionalNormalDisbursed:    r.additionalNormalDisbursed || 0,
          schoolFeesLoan:               r.schoolFeesLoan,
          schoolFeesInterest:           r.schoolFeesInterest,
          schoolFeesRepayment:          r.schoolFeesRepayment,
          additionalSchoolDisbursed:    r.additionalSchoolDisbursed || 0,
          emergencyLoan:                r.emergencyLoan,
          emergencyLoanInterest:        r.emergencyLoanInterest,
          emergencyLoanRepayment:       r.emergencyLoanRepayment,
          additionalEmergencyDisbursed: r.additionalEmergencyDisbursed || 0,
          applicationFees:              r.applicationFees,
          totalDeductions:              r.totalDeductions,
        }))
      };
      const r = await fetch(`${API_URL}/reports/save-statement-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload)
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message || 'Failed to save');
      setSaveMsg(`✅ ${j.message}`);
    } catch(e) {
      setSaveMsg(`❌ ${e.message || 'Failed to save records'}`);
    } finally {
      setSaving(false);
    }
  }

  async function toggleSavedView() {
    if (savedView) { setSavedView(false); setSavedData(null); return; }
    if (!memberId) return;
    try {
      setSavedView(true);
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/saved-statement-records/${memberId}?fromMonth=${fromMonth}&toMonth=${toMonth}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message);
      setSavedData(j.data);
    } catch(e) {
      setSavedView(false);
      setErr(e.message || 'Failed to load saved records');
    }
  }

  async function exportCSV(){
    if (!memberId) return;
    const token = localStorage.getItem('hopeSaccoToken');
    const url = `${API_URL}/reports/member-statement/${memberId}/export?fromMonth=${fromMonth}&toMonth=${toMonth}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || 'Export failed');
        return;
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `member-statement-${memberId}-${fromMonth}-${toMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export CSV');
    }
  }

  return (
    <div style={{ padding: '20px', maxWidth: '100%', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ color: '#4b3ff6', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
          Member Statement
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#666' }}>
          <span 
            onClick={() => navigate('/admin/dashboard')} 
            style={{ cursor: 'pointer', color: '#4b3ff6' }}
          >
            Dashboard
          </span>
          <span>›</span>
          <span style={{ color: '#333' }}>Member Statement</span>
        </div>
      </div>

      <div className="container" style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

        {err && <div className="alert alert-danger" style={{ marginTop: 10 }}>{err}</div>}

        <div style={{ display:'grid', gridTemplateColumns:'240px auto auto 1fr', gap:12, marginTop:10, alignItems:'end', flexWrap:'wrap' }}>
          <div>
            <label>Member *</label>
            <MemberSearch members={members} value={memberId} onChange={v => { setMemberId(v); setData(null); setSaveMsg(''); setSavedData(null); setSavedView(false); }} />
          </div>
          <div>
            <label>From (YYYY-MM) *</label>
            <input type="month" value={fromMonth} onChange={e=>setFromMonth(e.target.value)} />
          </div>
          <div>
            <label>To (YYYY-MM) *</label>
            <input type="month" value={toMonth} onChange={e=>setToMonth(e.target.value)} />
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
            <button onClick={loadStatement} disabled={loading || !memberId}>{loading ? 'Loading...' : 'Load Statement'}</button>
            <button onClick={exportCSV} disabled={!data}>Export CSV</button>
            <button
              onClick={saveRecords}
              disabled={!data || saving}
              style={{ background: '#16a34a', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
            >{saving ? 'Saving...' : '💾 Save Records'}</button>
            <button
              onClick={toggleSavedView}
              disabled={!memberId}
              style={{ background: savedView ? '#4b3ff6' : '#6b7280', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
            >{savedView ? '📋 Viewing Saved' : '📋 View Saved Records'}</button>
            <button
              onClick={() => navigate(`/admin/reports/merged-statement?memberId=${memberId}&fromMonth=${fromMonth}&toMonth=${toMonth}`)}
              disabled={!memberId}
              style={{ background: '#0891b2', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
            >🔀 Merged View</button>
          </div>
        </div>

        {data && data.member?.isLeft && (
          <div style={{
            background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
            padding: '12px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12
          }}>
            <span style={{ fontSize: 20 }}>🗂️</span>
            <div>
              <strong style={{ color: '#92400e' }}>Archived Member — Left SACCO</strong>
              {data.member.leftDate && (
                <span style={{ color: '#78350f', fontSize: 13, marginLeft: 10 }}>
                  Left on {new Date(data.member.leftDate).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
                </span>
              )}
              {data.member.leftReason && (
                <span style={{ color: '#78350f', fontSize: 13, marginLeft: 10 }}>
                  · Reason: {data.member.leftReason}
                </span>
              )}
              <div style={{ fontSize: 12, color: '#92400e', marginTop: 3 }}>
                Statement shown up to their exit month. All historical records are preserved.
              </div>
            </div>
          </div>
        )}
        {data && (
          <div style={{ marginTop:14, overflowX:'auto' }}>
            <table className="table table-striped">
              <thead>
                <tr>
                  <th style={{ paddingRight: '15px' }}>Month</th>
                  <th style={{ paddingRight: '15px' }}>Shares</th>
                  <th style={{ paddingRight: '15px' }}>Shares Contri.</th>
                  <th style={{ paddingRight: '15px' }}>Loan</th>
                  <th style={{ paddingRight: '15px' }}>Loan Int.</th>
                  <th>Repayment</th>
                  <th>Add. Normal</th>
                  <th>School Fee</th>
                  <th>School Int.</th>
                  <th>School Repayment</th>
                  <th>Add. School</th>
                  <th>Emerg. Loan</th>
                  <th>Emerg. Int.</th>
                  <th>Emerg. Repayment</th>
                  <th>Add. Emergency</th>
                  <th>Appl. Fees</th>
                  <th>Total Deductions</th>
                </tr>
              </thead>
              <tbody>
                {data.statement.map((r, i) => (
                  <tr key={i}>
                    <td style={{ paddingRight: '15px' }}>{r.month}</td>
                    <td className="text-right" style={{ paddingRight: '15px' }}>{fmt(r.shares,0)}</td>
                    <td className="text-right" style={{ paddingRight: '15px' }}>{fmt(r.sharesContribution,2)}</td>
                    <td className="text-right" style={{ paddingRight: '15px' }}>{fmt(r.loanBalance,2)}</td>
                    <td className="text-right" style={{ paddingRight: '15px' }}>{fmt(r.loanInterest,2)}</td>
                    <td className="text-right">{fmt(r.loanRepayment,0)}</td>
                    <td className="text-right">{fmt(r.additionalNormalDisbursed || 0,2)}</td>
                    <td className="text-right">{fmt(r.schoolFeesLoan,2)}</td>
                    <td className="text-right">{fmt(r.schoolFeesInterest,2)}</td>
                    <td className="text-right">{fmt(r.schoolFeesRepayment,0)}</td>
                    <td className="text-right">{fmt(r.additionalSchoolDisbursed || 0,2)}</td>
                    <td className="text-right">{fmt(r.emergencyLoan,2)}</td>
                    <td className="text-right">{fmt(r.emergencyLoanInterest,2)}</td>
                    <td className="text-right">{fmt(r.emergencyLoanRepayment,0)}</td>
                    <td className="text-right">{fmt(r.additionalEmergencyDisbursed || 0,2)}</td>
                    <td className="text-right">{fmt(r.applicationFees,2)}</td>
                    <td className="text-right"><b>{fmt(r.totalDeductions,2)}</b></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background:'#f7f7f7' }}>
                  <td style={{ paddingRight: '15px' }}><b>TOTALS</b></td>
                  <td className="text-right" style={{ paddingRight: '15px' }}><b>{fmt(data.summary.totalShares,2)}</b></td>
                  <td className="text-right" style={{ paddingRight: '15px' }}><b>{fmt(data.summary.totalSharesContributed,0)}</b></td>
                  <td style={{ paddingRight: '15px' }}></td>
                  <td className="text-right" style={{ paddingRight: '15px' }}><b>{fmt(data.summary.totalLoanInterest,2)}</b></td>
                  <td className="text-right"><b>{fmt(data.summary.totalLoanRepayments,0)}</b></td>
                  <td></td>
                  <td></td>
                  <td className="text-right"><b>{fmt(data.summary.totalSchoolInterest,2)}</b></td>
                  <td className="text-right"><b>{fmt(data.summary.totalSchoolRepayments,0)}</b></td>
                  <td></td>
                  <td></td>
                  <td className="text-right"><b>{fmt(data.summary.totalEmergencyInterest,2)}</b></td>
                  <td className="text-right"><b>{fmt(data.summary.totalEmergencyRepayments,0)}</b></td>
                  <td></td>
                  <td className="text-right"><b>{fmt(data.summary.totalApplicationFees,2)}</b></td>
                  <td className="text-right"><b>{fmt(data.summary.grandTotalDeductions,2)}</b></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {saveMsg && (
          <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 6, background: saveMsg.startsWith('✅') ? '#dcfce7' : '#fee2e2', color: saveMsg.startsWith('✅') ? '#15803d' : '#dc2626', fontWeight: 600 }}>
            {saveMsg}
          </div>
        )}

        {savedView && savedData && (
          <div style={{ marginTop: 20 }}>
            <h3 style={{ color: '#4b3ff6', marginBottom: 10 }}>
              📋 Saved Records — {savedData.summary.savedMonths} of {savedData.summary.totalMonths} months saved
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Month</th><th>Saved</th><th>Shares</th><th>Shares Contri.</th>
                    <th>Loan</th><th>Loan Int.</th><th>Repayment</th>
                    <th>School Fee</th><th>School Int.</th><th>School Repayment</th>
                    <th>Emerg. Loan</th><th>Emerg. Int.</th><th>Emerg. Repayment</th>
                    <th>Appl. Fees</th><th>Total Deductions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedData.statement.map((r, i) => (
                    <tr key={i} style={{ background: r.saved ? 'white' : '#fef9c3' }}>
                      <td>{r.month}</td>
                      <td style={{ textAlign:'center' }}>
                        {r.saved ? <span style={{ color:'#16a34a', fontWeight:600 }} title={`Saved by ${r.savedBy}`}>✅</span> : <span style={{ color:'#9ca3af' }}>—</span>}
                      </td>
                      <td className="text-right">{fmt(r.shares,0)}</td>
                      <td className="text-right">{fmt(r.sharesContribution,2)}</td>
                      <td className="text-right">{fmt(r.loanBalance,2)}</td>
                      <td className="text-right">{fmt(r.loanInterest,2)}</td>
                      <td className="text-right">{fmt(r.loanRepayment,0)}</td>
                      <td className="text-right">{fmt(r.schoolFeesLoan,2)}</td>
                      <td className="text-right">{fmt(r.schoolFeesInterest,2)}</td>
                      <td className="text-right">{fmt(r.schoolFeesRepayment,0)}</td>
                      <td className="text-right">{fmt(r.emergencyLoan,2)}</td>
                      <td className="text-right">{fmt(r.emergencyLoanInterest,2)}</td>
                      <td className="text-right">{fmt(r.emergencyLoanRepayment,0)}</td>
                      <td className="text-right">{fmt(r.applicationFees,2)}</td>
                      <td className="text-right"><b>{fmt(r.totalDeductions,2)}</b></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#f7f7f7' }}>
                    <td colSpan={2}><b>TOTALS</b></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalShares,0)}</b></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalSharesContributed,2)}</b></td>
                    <td></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalLoanInterest,2)}</b></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalLoanRepayments,0)}</b></td>
                    <td></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalSchoolInterest,2)}</b></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalSchoolRepayments,0)}</b></td>
                    <td></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalEmergencyInterest,2)}</b></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalEmergencyRepayments,0)}</b></td>
                    <td className="text-right"><b>{fmt(savedData.summary.totalApplicationFees,2)}</b></td>
                    <td className="text-right"><b>{fmt(savedData.summary.grandTotalDeductions,2)}</b></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {savedView && !savedData && (
          <div style={{ marginTop: 20, padding: 16, background: '#f3f4f6', borderRadius: 8, color: '#6b7280' }}>
            No saved records found for this member in the selected date range.
          </div>
        )}

      </div>
    </div>
  );
}